import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Markup, CreateMarkupRequest, UpdateMarkupRequest } from '../interfaces/Markup';
import { markupService } from '../services/markupService';

interface MarkupState {
  markups: Markup[];
  loading: boolean;
  error: string | null;
  filters: {
    status: 'all' | 'active' | 'inactive';
    type: 'all' | 'percentage' | 'fixed_value';
    target: 'all' | 'adult_price' | 'boarding_tax' | 'total_price';
    search: string;
  };
}

type MarkupAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_MARKUPS'; payload: Markup[] }
  | { type: 'ADD_MARKUP'; payload: Markup }
  | { type: 'UPDATE_MARKUP'; payload: Markup }
  | { type: 'DELETE_MARKUP'; payload: number }
  | { type: 'SET_FILTERS'; payload: Partial<MarkupState['filters']> }
  | { type: 'RESET_FILTERS' };

const initialState: MarkupState = {
  markups: [],
  loading: false,
  error: null,
  filters: {
    status: 'all',
    type: 'all',
    target: 'all',
    search: '',
  },
};

const markupReducer = (state: MarkupState, action: MarkupAction): MarkupState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_MARKUPS':
      return { ...state, markups: action.payload };
    case 'ADD_MARKUP':
      return { ...state, markups: [...state.markups, action.payload] };
    case 'UPDATE_MARKUP':
      return {
        ...state,
        markups: state.markups.map(markup =>
          markup.id === action.payload.id ? action.payload : markup
        ),
      };
    case 'DELETE_MARKUP':
      return {
        ...state,
        markups: state.markups.filter(markup => markup.id !== action.payload),
      };
    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };
    case 'RESET_FILTERS':
      return {
        ...state,
        filters: {
          status: 'all',
          type: 'all',
          target: 'all',
          search: '',
        },
      };
    default:
      return state;
  }
};

interface MarkupContextValue {
  state: MarkupState;
  actions: {
    loadMarkups: () => Promise<void>;
    createMarkup: (markup: CreateMarkupRequest) => Promise<void>;
    updateMarkup: (id: number, markup: UpdateMarkupRequest) => Promise<void>;
    deleteMarkup: (id: number) => Promise<void>;
    setFilters: (filters: Partial<MarkupState['filters']>) => void;
    resetFilters: () => void;
    getFilteredMarkups: () => Markup[];
    getMarkupStats: () => {
      total: number;
      active: number;
      inactive: number;
      percentage: number;
      fixedValue: number;
    };
  };
}

const MarkupContext = createContext<MarkupContextValue | undefined>(undefined);

export const useMarkup = (): MarkupContextValue => {
  const context = useContext(MarkupContext);
  if (!context) {
    throw new Error('useMarkup must be used within a MarkupProvider');
  }
  return context;
};

interface MarkupProviderProps {
  children: ReactNode;
}

export const MarkupProvider: React.FC<MarkupProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(markupReducer, initialState);

  const actions: MarkupContextValue['actions'] = {
    loadMarkups: async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });
        const markups = await markupService.getMarkups();
        dispatch({ type: 'SET_MARKUPS', payload: markups });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao carregar markups' });
        console.error('Erro ao carregar markups:', error);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    createMarkup: async (markup: CreateMarkupRequest) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        const newMarkup = await markupService.createMarkup(markup);
        dispatch({ type: 'ADD_MARKUP', payload: newMarkup });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao criar markup' });
        console.error('Erro ao criar markup:', error);
        throw error;
      }
    },

    updateMarkup: async (id: number, markup: UpdateMarkupRequest) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        const updatedMarkup = await markupService.updateMarkup(id, markup);
        dispatch({ type: 'UPDATE_MARKUP', payload: updatedMarkup });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao atualizar markup' });
        console.error('Erro ao atualizar markup:', error);
        throw error;
      }
    },

    deleteMarkup: async (id: number) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        await markupService.deleteMarkup(id);
        dispatch({ type: 'DELETE_MARKUP', payload: id });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao excluir markup' });
        console.error('Erro ao excluir markup:', error);
        throw error;
      }
    },

    setFilters: (filters: Partial<MarkupState['filters']>) => {
      dispatch({ type: 'SET_FILTERS', payload: filters });
    },

    resetFilters: () => {
      dispatch({ type: 'RESET_FILTERS' });
    },

    getFilteredMarkups: () => {
      let filtered = [...state.markups];

      if (state.filters.status !== 'all') {
        filtered = filtered.filter(markup =>
          state.filters.status === 'active' ? markup.is_active : !markup.is_active
        );
      }

      if (state.filters.type !== 'all') {
        filtered = filtered.filter(markup => markup.type === state.filters.type);
      }

      if (state.filters.target !== 'all') {
        filtered = filtered.filter(markup => markup.target === state.filters.target);
      }

      if (state.filters.search) {
        const searchLower = state.filters.search.toLowerCase();
        filtered = filtered.filter(markup =>
          markup.name.toLowerCase().includes(searchLower) ||
          markup.created_by.toLowerCase().includes(searchLower) ||
          (markup.origin_airport && markup.origin_airport.toLowerCase().includes(searchLower)) ||
          (markup.destination_airport && markup.destination_airport.toLowerCase().includes(searchLower))
        );
      }

      return filtered;
    },

    getMarkupStats: () => {
      const markups = state.markups;
      return {
        total: markups.length,
        active: markups.filter(m => m.is_active).length,
        inactive: markups.filter(m => !m.is_active).length,
        percentage: markups.filter(m => m.type === 'percentage').length,
        fixedValue: markups.filter(m => m.type === 'fixed_value').length,
      };
    },
  };

  return (
    <MarkupContext.Provider value={{ state, actions }}>
      {children}
    </MarkupContext.Provider>
  );
};